import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFlightConfig } from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import { IconNameEnum } from "@b2bportal/core-themes";
import { CoreFlightsComponent, SearchEvents } from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { trackEvent } from "@hopper-b2b/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TripCategory } from "@overrides/types";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  type CalendarTrackingProperties,
  useFlightSearch,
  CalendarPickerBottomContent,
  FlightSearchHooks,
} from "@tenant/flights";
import {
  Calendar,
  Dialog,
  DialogContent,
  PriceRangeTags,
  PickerType,
  MonthAndDatePickerType,
  Icon,
} from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import defaultStyles from "./CalendarPicker.module.scss";
import { TenantIconNameEnum } from "@/types/tenantIcons";
import { useViewedCalendarProperties } from "@/utilities/tracking/calendarTracking";

export interface ICalendarPickerProps {
  focusedMonthIndex?: number;
  ready?: boolean;
  calendarClassName?: string;
  setCalendarVisitedHandler?: (value: boolean) => void;
  calendarVisited?: boolean;
  open: boolean;
  onClose: () => void;
  onSearch?: () => void;
  preventSameDateSelection?: boolean;
  header?: React.ReactNode;
  index?: number;
  minAllowedDate?: Date;
  maxAllowedDate?: Date;
  showApplyButton?: boolean;
}

export const CalendarPicker = (props: ICalendarPickerProps) => {
  const {
    focusedMonthIndex,
    calendarVisited = false,
    setCalendarVisitedHandler,
    open,
    onClose,
    onSearch,
    preventSameDateSelection,
    index,
    minAllowedDate,
    maxAllowedDate,
    showApplyButton,
  } = props;

  const COMPONENT_KEY = CoreFlightsComponent.CalendarPicker;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const { brand } = useI18nContext();

  const [useA11yColors, setUseA11yColors] = useState(() => {
    try {
      const saved = localStorage.getItem("calendar-a11y-mode");
      if (!saved) return false;
      const parsed = JSON.parse(saved);
      return typeof parsed === "boolean" ? parsed : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem("calendar-a11y-mode", JSON.stringify(useA11yColors));
  }, [useA11yColors]);

  const {
    context: {
      origin,
      destination,
      departureDate,
      returnDate,
      tripCategory,
      passengerCounts,
    },
    handlers: { onDepartureDateChange, onReturnDateChange },
  } = useFlightSearch(index);

  const { showCalendarPriceRangeTags } = useFlightConfig();

  // Comprehensive tracking properties for RBC viewed_calendar event
  const comprehensiveTrackingProperties = useViewedCalendarProperties(
    origin,
    destination,
    tripCategory,
    passengerCounts
  );

  // Track initial dates when calendar opens to detect changes
  const [initialDates, setInitialDates] = useState<{
    departure?: string;
    return?: string;
  }>(() => ({
    departure: departureDate ?? undefined,
    return: returnDate ?? undefined,
  }));

  // Store tracking properties when calendar opens to avoid recreating handleClose
  const trackingPropertiesRef = useRef(comprehensiveTrackingProperties);

  // Capture initial dates and tracking properties when calendar opens
  useEffect(() => {
    if (open) {
      setInitialDates({
        departure: departureDate ?? undefined,
        return: returnDate ?? undefined,
      });
      // Capture tracking properties to avoid recreating handleClose when they change
      trackingPropertiesRef.current = comprehensiveTrackingProperties;
    }
    // IMPORTANT: departureDate and returnDate are intentionally excluded from dependencies
    // to capture the snapshot at calendar open time, not track ongoing changes.
    // This allows us to detect if dates changed when the calendar closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fire view_calendar event when dialog opens
  useEffect(() => {
    if (open && !calendarVisited) {
      if (setCalendarVisitedHandler) {
        setCalendarVisitedHandler(true);
      }

      // Track with comprehensive properties
      // Note: comprehensiveTrackingProperties is omitted from dependencies to prevent
      // re-firing when search params change. The guard (open && !calendarVisited)
      // ensures this only fires once per calendar open session.
      trackEvent({
        eventName: SearchEvents.VIEW_CALENDAR,
        properties: comprehensiveTrackingProperties,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, calendarVisited, setCalendarVisitedHandler]);

  const headerTitle = useMemo(
    () =>
      isDesktopAndUp
        ? tripCategory === TripCategory.ROUND_TRIP
          ? t("core-flights.search.dateSelection.title.full.roundTrip")
          : t("core-flights.search.dateSelection.title.full.oneWay")
        : tripCategory === TripCategory.ROUND_TRIP
          ? t("core-flights.search.dateSelection.title.short.roundTrip")
          : t("core-flights.search.dateSelection.title.short.oneWay"),
    [t, tripCategory, isDesktopAndUp]
  );

  const calendarClassName = useMemo(
    () =>
      tripCategory === TripCategory.ROUND_TRIP ? cn("roundtrip") : cn("oneWay"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tripCategory]
  );

  const ready = useMemo(() => {
    if (
      tripCategory === TripCategory.ONE_WAY ||
      tripCategory === TripCategory.MULTI_CITY
    ) {
      return Boolean(departureDate);
    }

    if (tripCategory === TripCategory.ROUND_TRIP) {
      return Boolean(departureDate && returnDate);
    }

    return false;
  }, [tripCategory, departureDate, returnDate]);

  const pickerType =
    tripCategory === TripCategory.ROUND_TRIP
      ? PickerType.RANGE
      : PickerType.DAY;

  // For multicity flights, pass the index to fetch calendar for the specific slice (leg)
  const { departureMonths, priceTags } = FlightSearchHooks.useFetchCalendar({
    origin,
    destination,
    tripCategory,
    sliceIndex: index,
  });

  // Handle calendar close - fire search_calendar_changed if dates changed
  const handleClose = useCallback(() => {
    const datesChanged =
      initialDates.departure !== (departureDate ?? undefined) ||
      initialDates.return !== (returnDate ?? undefined);

    if (datesChanged) {
      trackEvent({
        eventName: SearchEvents.SEARCH_CALENDAR_CHANGED,
        properties: trackingPropertiesRef.current,
      });
    }

    onClose();
  }, [initialDates, departureDate, returnDate, onClose]);

  const priceTagsElement = priceTags.length ? (
    <div className={cn("price-tags-section")}>
      <PriceRangeTags
        priceTags={priceTags}
        showTags={showCalendarPriceRangeTags}
      />
    </div>
  ) : null;

  const bottomContent = (
    <CalendarPickerBottomContent
      priceTagsElement={priceTagsElement}
      ready={ready}
      onClose={handleClose}
      onSearch={onSearch}
      showApplyButton={showApplyButton}
    />
  );

  const customHeader = (
    <div className={cn("header")}>
      <DialogPrimitive.Title className={cn("title")}>
        {headerTitle}
      </DialogPrimitive.Title>
      <div className={cn("header-actions")}>
        <button
          type="button"
          className={cn("accessibility-toggle", { active: useA11yColors })}
          onClick={() => setUseA11yColors(!useA11yColors)}
          aria-label={t(
            "core-flights.search.dateSelection.accessibilityToggle.label"
          )}
        >
          <Icon iconName={TenantIconNameEnum.accessibility} />
          <span className={cn("accessibility-toggle-text")}>
            {useA11yColors
              ? t("core-flights.search.dateSelection.accessibilityToggle.on")
              : t("core-flights.search.dateSelection.accessibilityToggle.off")}
          </span>
        </button>
        <DialogPrimitive.Close asChild>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            aria-label={t("close.button")}
          >
            <Icon iconName={IconNameEnum.close} />
          </button>
        </DialogPrimitive.Close>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent
        header={customHeader}
        className={clsx(block, calendarClassName, {
          [cn("a11y-mode")]: useA11yColors,
        })}
        fullScreen={!isDesktopAndUp}
        bottomFixedContent={bottomContent}
        showCloseButton={false}
      >
        <Calendar
          className={cn("calendar")}
          viewType={
            isDesktopAndUp
              ? MonthAndDatePickerType.Horizontal
              : MonthAndDatePickerType.Column
          }
          focusedMonthIndex={focusedMonthIndex}
          setStartDate={(date) => onDepartureDateChange(date?.toISOString())}
          setEndDate={(date) => onReturnDateChange(date?.toISOString())}
          startDate={
            departureDate == null ? null : dayjs(departureDate).toDate()
          }
          endDate={returnDate == null ? null : dayjs(returnDate).toDate()}
          months={departureMonths}
          priceTagsElement={priceTagsElement ?? <></>}
          showPriceTags={!isDesktopAndUp}
          priceTags={priceTags}
          pickerType={pickerType}
          currency={brand.currency.code}
          preventSameDateSelection={preventSameDateSelection}
          minAllowedDate={minAllowedDate}
          maxAllowedDate={maxAllowedDate}
          a11yModeEnabled={useA11yColors}
        />
      </DialogContent>
    </Dialog>
  );
};
